import { FC, useCallback, useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { BroadcastReplyMessage, BroadcastReplyType, MobileNumber } from 'types'
import getMobileNumber from 'utils/getMobileNumber'
import { Button, message, Row, Table, Tooltip } from 'antd'
import getFullName from 'utils/getFullName'
import { RiDownload2Fill } from 'react-icons/ri'
import useSingleBroadcast from 'hooks/useSingleBroadcast'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getBroadcastRepliesAction } from 'slices/broadcast'

import styles from 'component/Broadcast/BroadcastReplies/styles.module.scss'
import downloadUsingAPI from 'utils/downloadUsingAPI'
import getBroadcastReportExport from 'api/broadcasts/getBroadcastReportExport'
import handleAxiosError from 'utils/handleAxiosError'

const BroadcastFormReplies: FC = () => {
	const { replies, repliesLoading, repliesPagination, broadcast } =
		useSingleBroadcast()

	const [customLoading, setCustomLoading] = useState(false)

	const dispatch = useAppDispatch()

	const fetchReplies = useCallback(
		(page?: number) => {
			if (!broadcast) return

			dispatch(
				getBroadcastRepliesAction({
					broadcastId: broadcast._id,
					page: page ?? repliesPagination.currentPage ?? 1,
					size: repliesPagination.size ?? 10,
				})
			)
		},
		[broadcast, repliesLoading, dispatch, getBroadcastRepliesAction]
	)

	const validateBroadcast = () => {
		if (broadcast === null) {
			throw new Error('Broadcast is null')
		}
	}

	const handleExport = async () => {
		setCustomLoading(true)
		try {
			validateBroadcast()

			await downloadUsingAPI(
				await getBroadcastReportExport({ id: broadcast!._id, type: 'REPLY' }),
				`${broadcast!.name}-REPLY-REPORT.csv`
			)
			message.success(`Reply has been downloaded.`)
			setCustomLoading(false)
		} catch (error: any) {
			handleAxiosError(error)
			setCustomLoading(false)
		}
	}

	useEffect(() => {
		fetchReplies()
	}, [broadcast])

	const columns: ColumnsType<BroadcastReplyType> = [
		{
			title: 'Name',
			dataIndex: ['user', 'firstName'],
			render: (_: string, row: BroadcastReplyType) => getFullName(row.user),
		},
		{
			title: 'Number',
			dataIndex: ['user', 'mobile'],
			render: (mobile: MobileNumber) => getMobileNumber(mobile),
		},
		{
			title: 'Replies',
			dataIndex: 'message',

			ellipsis: {
				showTitle: false,
			},
			render: (msg: BroadcastReplyMessage) =>
				msg.message.type === 'TEXT' ? (
					<Tooltip
						placement='leftBottom'
						title={msg.message.message}
						color='#fff'
						overlayInnerStyle={{
							color: '#1d3142',
							padding: 15,
							lineHeight: 1.4,
						}}
					>
						{msg.message.message}
					</Tooltip>
				) : (
					'--'
				),
		},
	]

	return (
		<div>
			<Row justify='end' className={styles.toolRow}>
				<Button
					loading={customLoading}
					icon={<RiDownload2Fill />}
					type='primary'
					onClick={() => handleExport()}
				>
					Export
				</Button>
			</Row>
			<div>
				<Table
					dataSource={replies}
					columns={columns}
					loading={repliesLoading}
					pagination={{
						pageSize: repliesPagination.size,
						total: repliesPagination.totalCount,
						current: repliesPagination.currentPage,
						onChange: page => {
							fetchReplies(page)
						},
					}}
				/>
			</div>
		</div>
	)
}

export default BroadcastFormReplies
